import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { supabase } from '@/integrations/supabase/client';

// Extend jsPDF interface to include autoTable
declare module 'jspdf' {
  interface jsPDF {
    autoTable: (options: any) => jsPDF;
  }
}

interface Group {
  id: string;
  name: string;
  lecturer_name: string;
}

interface Student {
  id: string;
  first_name: string;
  last_name: string;
}

interface Phase {
  id: string;
  name: string;
  phase_number: number;
}

interface Lesson {
  id: string;
  lesson_number: number;
  lesson_date: string;
  topic?: string;
  time?: string;
  status?: string;
}

interface AttendanceRecord {
  lesson_id: string;
  student_id: string;
  is_present: boolean;
}

interface PhaseGrade {
  student_id: string;
  grade: number;
  phase_id: string;
}

interface GroupData {
  group: Group;
  students: Student[];
  phases: Phase[];
  lessons: { [phaseId: string]: Lesson[] };
  attendance: AttendanceRecord[];
  grades: PhaseGrade[];
}

export const fetchAllGroupsData = async (): Promise<GroupData[]> => {
  const allGroupsData: GroupData[] = [];

  // Fetch all groups with lecturer info
  const { data: groupsData, error: groupsError } = await supabase
    .from('groups')
    .select('id, name, lecturer_id')
    .order('name');

  if (groupsError) throw groupsError;
  if (!groupsData || groupsData.length === 0) return [];

  // Get lecturer profiles
  const lecturerIds = [...new Set(groupsData.map(g => g.lecturer_id))];
  const { data: lecturerProfiles } = await supabase
    .from('profiles')
    .select('id, first_name, last_name')
    .in('id', lecturerIds);

  for (const group of groupsData) {
    const lecturer = lecturerProfiles?.find(p => p.id === group.lecturer_id);
    const lecturerName = lecturer
      ? `${lecturer.first_name || ''} ${lecturer.last_name || ''}`.trim() || 'Unknown'
      : 'Unknown';

    // Fetch students for this group
    const { data: groupStudents } = await supabase
      .from('group_students')
      .select('student_id')
      .eq('group_id', group.id);

    let students: Student[] = [];
    if (groupStudents && groupStudents.length > 0) {
      const studentIds = groupStudents.map(gs => gs.student_id);
      const { data: studentProfiles } = await supabase
        .from('profiles')
        .select('id, first_name, last_name')
        .in('id', studentIds);
      students = studentProfiles || [];
    }

    // Fetch phases for this group
    const { data: phasesData } = await supabase
      .from('phases')
      .select('id, name, phase_number')
      .eq('group_id', group.id)
      .order('phase_number');

    const phases = phasesData || [];

    // Fetch lessons for each phase
    const lessons: { [phaseId: string]: Lesson[] } = {};
    for (const phase of phases) {
      const { data: lessonsData } = await supabase
        .from('lessons')
        .select('id, lesson_number, lesson_date, topic, time, status')
        .eq('phase_id', phase.id)
        .order('lesson_number');
      lessons[phase.id] = lessonsData || [];
    }

    // Fetch attendance for all lessons in this group
    const allLessonIds = Object.values(lessons).flat().map(l => l.id);
    let attendance: AttendanceRecord[] = [];
    if (allLessonIds.length > 0) {
      const { data: attendanceData } = await supabase
        .from('attendance')
        .select('lesson_id, student_id, is_present')
        .in('lesson_id', allLessonIds);
      attendance = attendanceData || [];
    }

    // Fetch phase grades
    const phaseIds = phases.map(p => p.id);
    let grades: PhaseGrade[] = [];
    if (phaseIds.length > 0) {
      const { data: gradesData } = await supabase
        .from('phase_grades')
        .select('student_id, grade, phase_id')
        .in('phase_id', phaseIds);
      grades = gradesData || [];
    }

    allGroupsData.push({
      group: { id: group.id, name: group.name, lecturer_name: lecturerName },
      students,
      phases,
      lessons,
      attendance,
      grades
    });
  }

  return allGroupsData;
};

export const exportElogToPDF = async (allGroupsData: GroupData[]) => {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  
  let isFirstPage = true;

  for (const groupData of allGroupsData) {
    const { group, students, phases, lessons, attendance, grades } = groupData;
    
    if (students.length === 0 || phases.length === 0) continue;

    if (!isFirstPage) {
      doc.addPage();
    }
    isFirstPage = false;

    // Group title
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text(`${group.name} - ${group.lecturer_name}`, 14, 15);
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Exported: ${new Date().toLocaleDateString('hy-AM')}`, 14, 22);

    let currentY = 30;

    for (const phase of phases) {
      const phaseLessons = lessons[phase.id] || [];
      if (phaseLessons.length === 0) continue;

      // Check if we need a new page
      if (currentY > 180) {
        doc.addPage();
        currentY = 20;
      }

      // Phase title
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text(`${phase.name}`, 14, currentY);
      currentY += 5;

      // Build table headers
      const headers = ['Student'];
      phaseLessons.forEach(lesson => {
        const dateStr = lesson.lesson_date 
          ? new Date(lesson.lesson_date).toLocaleDateString('hy-AM', { month: 'numeric', day: 'numeric' })
          : `L${lesson.lesson_number}`;
        headers.push(dateStr);
      });
      headers.push('Grade');
      headers.push('%');

      // Build table body
      const body = students.map(student => {
        const row: string[] = [`${student.first_name} ${student.last_name}`];
        
        let presentCount = 0;
        let totalLessons = 0;

        phaseLessons.forEach(lesson => {
          const record = attendance.find(
            a => a.lesson_id === lesson.id && a.student_id === student.id
          );
          if (record !== undefined) {
            totalLessons++;
            if (record.is_present) {
              presentCount++;
              row.push('+');
            } else {
              row.push('-');
            }
          } else {
            row.push('');
          }
        });

        // Phase grade
        const gradeRecord = grades.find(g => g.phase_id === phase.id && g.student_id === student.id);
        row.push(gradeRecord ? gradeRecord.grade.toString() : '-');

        // Attendance percentage
        const percentage = totalLessons > 0 ? Math.round((presentCount / totalLessons) * 100) : 0;
        row.push(`${percentage}%`);

        return row;
      });

      doc.autoTable({
        head: [headers],
        body: body,
        startY: currentY,
        styles: { 
          fontSize: 7,
          cellPadding: 1.5,
          overflow: 'linebreak'
        },
        headStyles: {
          fillColor: [34, 139, 34],
          textColor: 255,
          fontStyle: 'bold',
          fontSize: 6
        },
        columnStyles: {
          0: { cellWidth: 40 }
        },
        margin: { left: 14, right: 14 },
        theme: 'grid'
      });

      currentY = (doc as any).lastAutoTable.finalY + 10;
    }
  }

  // Save the PDF
  const timestamp = new Date().toISOString().slice(0, 10);
  doc.save(`e-journal-full-${timestamp}.pdf`);
};

export const exportElogToExcel = async (allGroupsData: GroupData[]) => {
  const workbook = XLSX.utils.book_new();

  for (const groupData of allGroupsData) {
    const { group, students, phases, lessons, attendance, grades } = groupData;
    
    if (students.length === 0 || phases.length === 0) continue;

    // Create a sheet for each group
    const sheetData: any[][] = [];
    
    // Group header
    sheetData.push([`Group: ${group.name}`]);
    sheetData.push([`Lecturer: ${group.lecturer_name}`]);
    sheetData.push([`Export Date: ${new Date().toLocaleDateString('hy-AM')}`]);
    sheetData.push([]);

    for (const phase of phases) {
      const phaseLessons = lessons[phase.id] || [];
      if (phaseLessons.length === 0) continue;

      // Phase header
      sheetData.push([`${phase.name}`]);

      // Headers row
      const headers = ['Student'];
      phaseLessons.forEach(lesson => {
        const dateStr = lesson.lesson_date 
          ? new Date(lesson.lesson_date).toLocaleDateString('hy-AM')
          : `Lesson ${lesson.lesson_number}`;
        headers.push(dateStr);
      });
      headers.push('Grade');
      headers.push('Attendance %');
      sheetData.push(headers);

      // Student rows
      students.forEach(student => {
        const row: (string | number)[] = [`${student.first_name} ${student.last_name}`];
        
        let presentCount = 0;
        let totalLessons = 0;

        phaseLessons.forEach(lesson => {
          const record = attendance.find(
            a => a.lesson_id === lesson.id && a.student_id === student.id
          );
          if (record !== undefined) {
            totalLessons++;
            if (record.is_present) {
              presentCount++;
              row.push('+');
            } else {
              row.push('-');
            }
          } else {
            row.push('');
          }
        });

        // Phase grade
        const gradeRecord = grades.find(g => g.phase_id === phase.id && g.student_id === student.id);
        row.push(gradeRecord ? gradeRecord.grade : '-');

        // Attendance percentage
        const percentage = totalLessons > 0 ? Math.round((presentCount / totalLessons) * 100) : 0;
        row.push(`${percentage}%`);

        sheetData.push(row);
      });

      sheetData.push([]);
    }

    // Create worksheet and add to workbook
    const worksheet = XLSX.utils.aoa_to_sheet(sheetData);
    
    // Truncate sheet name to 31 characters (Excel limit)
    const sheetName = group.name.substring(0, 31);
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
  }

  // Save the Excel file
  const timestamp = new Date().toISOString().slice(0, 10);
  XLSX.writeFile(workbook, `e-journal-full-${timestamp}.xlsx`);
};
